import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ListOrdered, RefreshCw, Search, Settings2, Download, FileCheck2, X, DollarSign, Calculator,
} from "lucide-react";
import {
  api, getUser, isDemoWorkspace, DOC_TYPES, PRIORITY_ORDER,
  type ConversionState, type Discovered, type DiscoveredEstimate, type DiscoveredFilters,
  type DiscoveredSortField, type DocType, type PlanSettings, type PlanSummary, type Priority,
  type PriorityOverride, type Site,
} from "../api";
import { Pagination } from "../components/Pagination";
import { SortHeader, type SortOrder } from "../components/SortHeader";
import { useConfirm } from "../components/Modal";
import { Alert, MoreMenu, PageHeader } from "../components/Page";
import { PlanSettingsDrawer } from "../components/PlanSettingsDrawer";
import {
  DocumentCell, EvidenceCell, PriorityCell, ReasonsCell, StatusCell, TypeCell,
} from "../components/PlanCells";
import {
  DOC_TYPE_LABEL, PRIORITY_META, PriorityPill, effectivePriority, formatUsd, isOutOfDate,
  notConvertibleReason, readPlanSite, rowPages, writePlanSite,
} from "../components/planMeta";

type StateFilter = "any" | "not_converted" | "converted" | "queued" | "converting" | "live" | "review" | "failed" | "ignored";
const STATE_OPTIONS: { key: StateFilter; label: string }[] = [
  { key: "any", label: "Any status" },
  { key: "not_converted", label: "Not converted" },
  { key: "converted", label: "Converted (any state)" },
  { key: "queued", label: "Queued" },
  { key: "converting", label: "Converting" },
  { key: "live", label: "Live" },
  { key: "review", label: "Review needed" },
  { key: "failed", label: "Failed" },
  { key: "ignored", label: "Ignored" },
];
/** List tabs. Counts come from the plan's inventory (same as the Discovered tabs). */
type ListTab = "any" | "not_converted" | "converted";
const LIST_TABS: { key: ListTab; label: string }[] = [
  { key: "any", label: "Total" },
  { key: "not_converted", label: "Not converted" },
  { key: "converted", label: "Converted" },
];
const DEFAULT_STATE: StateFilter = "not_converted";
/** Which tab a Status choice belongs to (queued / ignored only show under Total). */
function tabOf(s: StateFilter): ListTab {
  if (s === "not_converted") return "not_converted";
  if (s === "converted" || s === "converting" || s === "live" || s === "review" || s === "failed") return "converted";
  return "any";
}

function stateToFilters(s: StateFilter): Pick<DiscoveredFilters, "status_filter" | "conversion_state"> {
  switch (s) {
    case "not_converted": return { status_filter: "discovered" };
    case "converted": return { status_filter: "converted" };
    case "queued": return { status_filter: "selected" };
    case "ignored": return { status_filter: "ignored" };
    case "converting": case "live": case "review": case "failed":
      return { status_filter: "all", conversion_state: [s as ConversionState] };
    default: return { status_filter: "all" };
  }
}

const OVERRIDE_OPTIONS: PriorityOverride[] = ["now", "next", "later", "archive", "never"];
/** Priorities the budget planner ranks; archive / duplicate / superseded / never / unscored are left out. */
const BUDGET_PRIORITIES: Priority[] = ["now", "next", "later"];
const SELECT_CAP = 2000;

interface Candidate { id: string; pages: number | null; priority: Priority }

function errMsg(e: unknown): string { return e instanceof Error ? e.message : String(e); }

/** The convert endpoint returns DiscoveredOut rows; skipped rows are reported
 *  either as an explicit `skipped` list or by being absent / not selected. */
function readConvertResult(resp: unknown, requested: string[]): { queued: number; skippedIds: string[] } {
  if (Array.isArray(resp)) {
    const rows = resp as Discovered[];
    const ok = new Set(rows.filter((r) => r.status === "selected" || r.status === "converted").map((r) => r.id));
    return { queued: ok.size, skippedIds: requested.filter((id) => !ok.has(id)) };
  }
  if (resp && typeof resp === "object") {
    const o = resp as Record<string, unknown>;
    const skipped = Array.isArray(o.skipped)
      ? (o.skipped as unknown[]).map((x) => (typeof x === "string" ? x : String((x as { id?: unknown })?.id ?? ""))).filter(Boolean)
      : [];
    const list = (o.converted ?? o.queued ?? o.items) as unknown;
    const queued = Array.isArray(list) ? list.length : typeof list === "number" ? list : requested.length - skipped.length;
    return { queued, skippedIds: skipped };
  }
  return { queued: requested.length, skippedIds: [] };
}

export function PlanPage() {
  const demo = isDemoWorkspace();
  const isSuper = getUser()?.role === "superadmin";
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [siteId, setSiteId] = useState<string>("");
  const [plan, setPlan] = useState<PlanSummary | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  const [rows, setRows] = useState<Discovered[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");

  const [prioFilter, setPrioFilter] = useState<Priority[]>([]);
  const [docType, setDocType] = useState<DocType | "">("");
  const [stateFilter, setStateFilter] = useState<StateFilter>(DEFAULT_STATE);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<DiscoveredSortField>("priority_score");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Every row we've seen (current page + budget candidates) — used to name
  // skipped rows after a convert even when they're not on this page.
  const known = useRef(new Map<string, Discovered>());

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirm, confirmNode] = useConfirm();

  const site = sites.find((s) => s.id === siteId) ?? null;

  // ── Sites ──
  useEffect(() => {
    api.listSites()
      .then((s) => {
        setSites(s);
        const stored = readPlanSite();
        const pick = s.find((x) => x.id === stored)?.id ?? s[0]?.id ?? "";
        setSiteId(pick);
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setSitesLoaded(true));
  }, []);

  function chooseSite(id: string) {
    setSiteId(id);
    writePlanSite(id);
    setSelected(new Set());
    setPrioFilter([]);
    setCandidates(null);
  }

  // ── Plan summary ──
  const refreshPlan = useCallback(async () => {
    if (!siteId) return;
    try {
      setPlan(await api.getPlan(siteId));
      setPlanError(null);
    } catch (e) {
      setPlan(null);
      setPlanError(errMsg(e));
    }
  }, [siteId]);
  useEffect(() => { refreshPlan(); }, [refreshPlan]);

  // ── Rows ──
  const listFilters = useMemo<DiscoveredFilters>(() => ({
    site_id: siteId,
    q: q || undefined,
    priority: prioFilter.length ? prioFilter : undefined,
    doc_type: docType || undefined,
    ...stateToFilters(stateFilter),
  }), [siteId, q, prioFilter, docType, stateFilter]);

  const refreshRows = useCallback(async () => {
    if (!siteId) { setLoading(false); return; }
    try {
      const data = await api.listDiscovered({ ...listFilters, sort, order, limit: perPage, offset: page * perPage });
      setRows(data.items);
      setTotal(data.total);
      for (const r of data.items) known.current.set(r.id, r);
      setError(null);
      setAnnounce(`${data.total.toLocaleString()} document${data.total === 1 ? "" : "s"} match the current filters.`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [siteId, listFilters, sort, order, page, perPage]);

  useEffect(() => { setPage(0); }, [listFilters, sort, order, perPage]);
  useEffect(() => { refreshRows(); }, [refreshRows]);

  // Poll while something on this page is in motion.
  useEffect(() => {
    const moving = rows.some((r) => r.conversion_state === "converting" || r.status === "selected" || r.scan_status === "pending");
    if (!moving) return;
    const t = window.setInterval(refreshRows, 5000);
    return () => window.clearInterval(t);
  }, [rows, refreshRows]);

  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(null), 6000);
    return () => window.clearTimeout(t);
  }, [success]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshRows(), refreshPlan()]);
  }, [refreshRows, refreshPlan]);

  function onSort(field: DiscoveredSortField, next: SortOrder) { setSort(field); setOrder(next); }

  function togglePrio(p: Priority) {
    setPrioFilter((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  }

  // ── Selection ──
  const selectable = rows.filter((r) => !notConvertibleReason(r));
  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id));
  function toggleAll() {
    setSelected((s) => {
      const next = new Set(s);
      if (allSelected) selectable.forEach((r) => next.delete(r.id));
      else selectable.forEach((r) => next.add(r.id));
      return next;
    });
  }
  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const [estimate, setEstimate] = useState<DiscoveredEstimate | null>(null);
  useEffect(() => {
    if (selected.size === 0) { setEstimate(null); return; }
    let cancelled = false;
    api.estimateDiscovered(Array.from(selected))
      .then((e) => { if (!cancelled) setEstimate(e); })
      .catch(() => { if (!cancelled) setEstimate(null); });
    return () => { cancelled = true; };
  }, [selected]);

  // ── Budget planner ──
  const [budgetInput, setBudgetInput] = useState("");
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState<string | null>(null);
  const [candTruncated, setCandTruncated] = useState(false);

  // Pre-fill from the saved page budget (only if the admin hasn't typed one).
  useEffect(() => {
    const b = plan?.settings?.page_budget;
    if (b != null) setBudgetInput((cur) => (cur ? cur : String(b)));
  }, [plan]);

  const budget = Number(budgetInput);
  const budgetValid = budgetInput.trim() !== "" && Number.isFinite(budget) && budget > 0;

  useEffect(() => {
    if (!budgetValid || !siteId || candidates) return;
    let cancelled = false;
    (async () => {
      setCandLoading(true); setCandError(null);
      try {
        const out: Candidate[] = [];
        let total = Infinity;
        for (let offset = 0; offset < SELECT_CAP && offset < total; offset += 500) {
          const pg = await api.listDiscovered({
            site_id: siteId, status_filter: "discovered", priority: BUDGET_PRIORITIES,
            sort: "priority_score", order: "desc", limit: 500, offset,
          });
          total = pg.total;
          for (const r of pg.items) {
            known.current.set(r.id, r);
            if (notConvertibleReason(r)) continue;
            out.push({ id: r.id, pages: rowPages(r), priority: effectivePriority(r) });
          }
          if (pg.items.length < 500) break;
        }
        if (!cancelled) { setCandidates(out); setCandTruncated(total > SELECT_CAP); }
      } catch (e) {
        if (!cancelled) setCandError(errMsg(e));
      } finally {
        if (!cancelled) setCandLoading(false);
      }
    })();
    return () => { cancelled = true; setCandLoading(false); };
  }, [budgetValid, siteId, candidates]);

  const fit = useMemo(() => {
    if (!budgetValid || !candidates) return null;
    const ids: string[] = [];
    const byPrio: Partial<Record<Priority, number>> = {};
    let pages = 0; let unknown = 0;
    for (const c of candidates) {
      if (c.pages == null) { unknown += 1; continue; }
      if (pages + c.pages > budget) break;
      pages += c.pages;
      ids.push(c.id);
      byPrio[c.priority] = (byPrio[c.priority] ?? 0) + 1;
    }
    return { ids, idSet: new Set(ids), pages, unknown, byPrio };
  }, [budgetValid, budget, candidates]);

  // Rankings change after convert / priority edits / re-score.
  const invalidateCandidates = () => setCandidates(null);

  // ── Actions ──
  async function convertIds(ids: string[]) {
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Convert ${ids.length} document${ids.length === 1 ? "" : "s"}?`,
      message: ids.length === selected.size && estimate
        ? <>About <strong>{estimate.estimated_pages.toLocaleString()}</strong> pages{isSuper && <> ({formatUsd(estimate.estimated_cost_usd)})</>}. Private uploads and documents marked Never are skipped.</>
        : <>The selected PDFs will be queued for conversion. Private uploads and documents marked Never are skipped.</>,
      confirmLabel: "Convert",
    });
    if (!ok) return;
    try {
      const resp: unknown = await api.convertDiscovered(ids);
      const { queued, skippedIds } = readConvertResult(resp, ids);
      setSelected(new Set());
      invalidateCandidates();
      if (skippedIds.length) {
        const names = skippedIds.slice(0, 8).map((id) => {
          const r = known.current.get(id);
          if (!r) return id.slice(0, 8);
          const why = r.is_private ? "private upload" : effectivePriority(r) === "never" ? "priority Never" : r.status === "converted" ? "already converted" : "not eligible";
          return `${r.filename} (${why})`;
        });
        setError(`Skipped ${skippedIds.length}: ${names.join(", ")}${skippedIds.length > 8 ? ", …" : ""}`);
      }
      if (queued > 0) setSuccess(`Queued ${queued} document${queued === 1 ? "" : "s"} for conversion.`);
      await refreshAll();
    } catch (e) { setError(errMsg(e)); }
  }

  async function ignoreIds(ids: string[]) {
    if (ids.length === 0) return;
    try {
      await api.ignoreDiscovered(ids);
      setSelected(new Set());
      invalidateCandidates();
      setSuccess(`Ignored ${ids.length} document${ids.length === 1 ? "" : "s"}.`);
      await refreshAll();
    } catch (e) { setError(errMsg(e)); }
  }

  async function overrideIds(ids: string[], value: string) {
    if (ids.length === 0 || !value) return;
    const p = value === "clear" ? null : (value as PriorityOverride);
    try {
      await api.setPriority(ids, p);
      invalidateCandidates();
      setSuccess(p ? `Set ${ids.length} to ${PRIORITY_META[p].label}.` : `Cleared the override on ${ids.length}.`);
      if (p === "never") setSelected((s) => { const n = new Set(s); ids.forEach((id) => n.delete(id)); return n; });
      await refreshAll();
    } catch (e) { setError(errMsg(e)); }
  }

  async function exportCsv() {
    if (!site) return;
    try { await api.exportPlanCsv(site.id, prioFilter.length ? prioFilter : undefined, `conversion-plan-${site.domain}`); }
    catch (e) { setError(errMsg(e)); }
  }

  async function rescore() {
    if (!siteId) return;
    const ok = await confirm({
      title: "Re-score this site?",
      message: "Recomputes every document's priority from the details already collected. Manual overrides are kept. Nothing is converted.",
      confirmLabel: "Re-score",
    });
    if (!ok) return;
    try {
      const r = await api.rescoreSite(siteId);
      setSuccess(`Re-scoring ${r?.queued != null ? `${r.queued} ` : ""}documents — the plan updates in a moment.`);
      afterBackgroundJob();
    } catch (e) { setError(errMsg(e)); }
  }

  async function refreshFacts() {
    if (!siteId) return;
    const ok = await confirm({
      title: "Re-download every PDF?",
      message: "Downloads each PDF on this site again to re-read its title, dates, form fields and text, then re-scores. Downloads are throttled to be gentle on the website, so large sites can take a while.",
      confirmLabel: "Refresh PDF details",
      tone: "warning",
    });
    if (!ok) return;
    try {
      const r = await api.refreshFacts(siteId);
      setSuccess(`Refreshing details for ${r?.queued ?? 0} PDFs. Priorities update as each one finishes.`);
      afterBackgroundJob();
    } catch (e) { setError(errMsg(e)); }
  }

  function afterBackgroundJob() {
    invalidateCandidates();
    window.setTimeout(() => { refreshAll(); }, 3000);
  }

  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  function onSettingsSaved(s: PlanSettings) {
    setSettingsOpen(false);
    setPlan((p) => (p ? { ...p, settings: s } : p));
    if (s.page_budget != null) setBudgetInput(String(s.page_budget));
    setSuccess("Settings saved — re-scoring documents.");
    afterBackgroundJob();
  }

  // Duplicate helpers for the current page.
  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const mirrorsOf = useMemo(() => {
    const m = new Map<string, Discovered[]>();
    for (const r of rows) if (r.duplicate_of) m.set(r.duplicate_of, [...(m.get(r.duplicate_of) ?? []), r]);
    return m;
  }, [rows]);

  const filtersActive = prioFilter.length > 0 || !!docType || (stateFilter !== "any" && stateFilter !== tabOf(stateFilter)) || !!q;
  function clearFilters() { setPrioFilter([]); setDocType(""); setStateFilter(tabOf(stateFilter)); setQ(""); setSearch(""); }
  const tabCount = (t: ListTab): number | null => {
    const inv = plan?.inventory;
    if (!inv) return null;
    return t === "any" ? inv.all : t === "converted" ? inv.converted : inv.not_converted;
  };

  const groups = plan?.groups ?? [];
  const groupFor = (p: Priority) => groups.find((g) => g.priority === p);

  // ── Render ──
  if (sitesLoaded && sites.length === 0) {
    return (
      <div>
        <PlanHeader />
        <div className="card"><div className="card-body text-sm text-slate-600">
          No sites yet. <Link to="/sites" className="text-brand-600 hover:underline">Add a site</Link> and run a crawl to build a conversion plan.
        </div></div>
      </div>
    );
  }

  return (
    <div>
      <PlanHeader
        actions={
          <>
            <label htmlFor="plan-site" className="sr-only">Site</label>
            <select id="plan-site" className="input !w-auto" value={siteId} onChange={(e) => chooseSite(e.target.value)}>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.domain}</option>)}
            </select>
            {!demo && (
              <button onClick={() => setSettingsOpen(true)} className="btn-secondary" disabled={!siteId}><Settings2 className="h-4 w-4" aria-hidden="true" /> Settings</button>
            )}
            <MoreMenu
              label="More plan actions"
              items={[
                { label: "Re-score this site", icon: <RefreshCw className="h-4 w-4" aria-hidden="true" />, onClick: rescore, hidden: demo, disabled: !siteId },
                { label: "Export CSV", icon: <Download className="h-4 w-4" aria-hidden="true" />, onClick: exportCsv, disabled: !siteId },
              ]}
            />
          </>
        }
      />

      {success && <Alert tone="success" onDismiss={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      {/* Summary strip */}
      <section aria-labelledby="plan-summary-title" className="mb-6">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <div>
            <h2 id="plan-summary-title" className="text-sm font-semibold text-slate-700">Plan summary <span className="font-normal text-slate-500">(not converted yet, or changed since it was converted)</span></h2>
            {plan?.inventory && (
              <p className="text-xs text-slate-500 tabular-nums mt-0.5">
                <strong className="text-slate-700">{plan.inventory.all.toLocaleString()} PDFs</strong> on this site
                {" · "}{plan.inventory.converted.toLocaleString()} converted
                {plan.inventory.queued > 0 && <>{" · "}{plan.inventory.queued.toLocaleString()} queued</>}
                {plan.inventory.ignored > 0 && <>{" · "}{plan.inventory.ignored.toLocaleString()} ignored</>}
                {" · "}{plan.inventory.not_converted.toLocaleString()} not converted
              </p>
            )}
          </div>
          <div className="text-xs text-slate-500">
            {plan?.scored_at ? <>Last scored {new Date(plan.scored_at).toLocaleString()}</> : plan ? "Not scored yet" : null}
            {plan?.settings?.compliance_date && <> · compliance date {new Date(`${plan.settings.compliance_date.slice(0, 10)}T00:00:00`).toLocaleDateString()}</>}
          </div>
        </div>
        {planError ? (
          <div className="card"><div className="card-body text-sm text-slate-500">Plan summary unavailable: {planError}</div></div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 sm:overflow-visible">
            {PRIORITY_ORDER.map((p) => {
              const g = groupFor(p);
              const active = prioFilter.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePrio(p)}
                  aria-pressed={active}
                  title={PRIORITY_META[p].help}
                  className={`card shrink-0 min-w-[8.5rem] sm:min-w-0 text-left px-3 py-2.5 transition-colors hover:border-brand-300 ${active ? "!border-brand-500 ring-2 ring-brand-500/30" : ""}`}
                >
                  <PriorityPill priority={p} />
                  <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">{g ? g.count.toLocaleString() : "—"}</div>
                  <div className="text-xs text-slate-500 tabular-nums">
                    {g ? <>{g.pages.toLocaleString()} pages{g.unknown_pages > 0 && <span title="Documents with unknown page count"> (+{g.unknown_pages} unknown)</span>}</> : " "}
                  </div>
                  {isSuper && g && <div className="text-xs text-slate-500 tabular-nums">{formatUsd(g.est_cost_usd)}</div>}
                </button>
              );
            })}
            <div className="card shrink-0 min-w-[8.5rem] sm:min-w-0 px-3 py-2.5 bg-slate-50" title="Documents to convert: not converted yet, leaving out duplicates, older versions and Never (they are never converted on their own).">
              <div className="text-xs font-semibold text-slate-600">To convert</div>
              <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">{plan ? plan.totals.count.toLocaleString() : "—"}</div>
              <div className="text-xs text-slate-500 tabular-nums">{plan ? `${plan.totals.pages.toLocaleString()} pages` : " "}</div>
              {isSuper && plan && <div className="text-xs text-slate-500 tabular-nums">{formatUsd(plan.totals.est_cost_usd)}</div>}
            </div>
          </div>
        )}
      </section>

      {/* Budget planner */}
      <section className="card mb-6" aria-labelledby="budget-title">
        <div className="card-body py-4 flex flex-wrap items-end gap-4">
          <div>
            <h2 id="budget-title" className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1">
              <Calculator className="h-4 w-4 text-brand-600" aria-hidden="true" /> Budget planner
            </h2>
            <label htmlFor="budget-pages" className="block text-xs text-slate-600 mb-1">Pages to convert</label>
            <input
              id="budget-pages" type="number" min={1} step={1} inputMode="numeric" className="input !w-40"
              value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)} placeholder="e.g. 2000"
              aria-describedby="budget-result"
            />
          </div>
          <div id="budget-result" className="text-sm text-slate-600 w-full sm:w-auto sm:flex-1 sm:min-w-[16rem]" aria-live="polite">
            {!budgetValid ? (
              <span className="text-slate-500">Enter a page budget to see which top-ranked documents fit (Convert now → next → later, highest score first). Nothing converts until you choose.</span>
            ) : candLoading ? (
              <span>Ranking documents…</span>
            ) : candError ? (
              <span className="text-rose-700">Couldn't rank documents: {candError}</span>
            ) : fit ? (
              <>
                <strong className="text-ink">{fit.ids.length.toLocaleString()}</strong> document{fit.ids.length === 1 ? "" : "s"} ·{" "}
                <strong className="text-ink">{fit.pages.toLocaleString()}</strong> pages fit
                {fit.ids.length > 0 && (
                  <span className="text-slate-500"> ({BUDGET_PRIORITIES.filter((p) => fit.byPrio[p]).map((p) => `${PRIORITY_META[p].label} ${fit.byPrio[p]}`).join(", ")})</span>
                )}
                {fit.unknown > 0 && <span className="text-slate-500"> · {fit.unknown} with unknown page count left out</span>}
                {candTruncated && <span className="text-slate-500"> · only the top {SELECT_CAP.toLocaleString()} were considered</span>}
                <div className="text-xs text-slate-500 mt-0.5">Rows that fit are marked “in budget” in the table.</div>
              </>
            ) : null}
          </div>
          <button
            type="button" className="btn-secondary"
            disabled={!fit || fit.ids.length === 0}
            onClick={() => fit && setSelected(new Set(fit.ids))}
          >
            Select these
          </button>
        </div>
      </section>

      {/* List tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1 overflow-x-auto" role="tablist" aria-label="Show documents">
        {LIST_TABS.map((t) => {
          const active = tabOf(stateFilter) === t.key;
          const n = tabCount(t.key);
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => { setStateFilter(t.key); setPage(0); }}
              className={
                "px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 " +
                (active ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-700")
              }
            >
              {t.label}{n != null && <span className="ml-1.5 text-xs text-slate-400 tabular-nums">{n.toLocaleString()}</span>}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => { e.preventDefault(); setQ(search.trim()); }}
        className="filter-bar"
        aria-label="Filter documents"
      >
        <fieldset className="min-w-0">
          <legend className="block text-xs font-medium text-slate-600 mb-1">Priority</legend>
          <div className="flex flex-wrap gap-1">
            {PRIORITY_ORDER.map((p) => (
              <button
                key={p} type="button" aria-pressed={prioFilter.includes(p)} onClick={() => togglePrio(p)}
                className={`px-2 py-1 text-xs rounded-full ring-1 ring-inset ${prioFilter.includes(p) ? "bg-brand-500 text-white ring-brand-500" : "bg-white text-slate-600 ring-slate-300 hover:bg-slate-50"}`}
              >
                {PRIORITY_META[p].short}
              </button>
            ))}
          </div>
        </fieldset>
        <div>
          <label htmlFor="plan-type" className="block text-xs font-medium text-slate-600 mb-1">Type</label>
          <select id="plan-type" className="input" value={docType} onChange={(e) => setDocType(e.target.value as DocType | "")}>
            <option value="">All types</option>
            {DOC_TYPES.map((t) => <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="plan-state" className="block text-xs font-medium text-slate-600 mb-1">Status</label>
          <select id="plan-state" className="input" value={stateFilter} onChange={(e) => setStateFilter(e.target.value as StateFilter)}>
            {STATE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="filter-grow">
          <label htmlFor="plan-search" className="block text-xs font-medium text-slate-600 mb-1">Search filename or URL</label>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input id="plan-search" className="input pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="permit, agenda, budget…" />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-secondary flex-1 sm:flex-none">Apply</button>
          {filtersActive && <button type="button" className="btn-ghost" onClick={clearFilters}>Clear</button>}
        </div>
      </form>

      <div className="sr-only" role="status" aria-live="polite">{announce}</div>
      {!loading && (
        <div className="mb-3 text-sm text-slate-600">
          <strong className="text-ink tabular-nums">{total.toLocaleString()}</strong> document{total === 1 ? "" : "s"}
          {filtersActive && <span className="text-slate-400"> matching current filters</span>}
        </div>
      )}

      {/* Bulk bar */}
      {selected.size > 0 && !demo && (
        <div className="card bg-brand-50 border-brand-200 mb-4">
          <div className="card-body py-3 flex flex-wrap items-center gap-3">
            <strong className="text-brand-900">{selected.size} selected</strong>
            {estimate && (
              <span className="text-xs text-brand-900/70 inline-flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
                {estimate.estimated_pages.toLocaleString()} pages
                {isSuper && <> · {formatUsd(estimate.estimated_cost_usd)}</>}
                {estimate.unknown_size_rows > 0 && <span className="text-slate-500"> ({estimate.unknown_size_rows} unknown size)</span>}
              </span>
            )}
            <button onClick={() => convertIds(Array.from(selected))} className="btn-primary"><FileCheck2 className="h-4 w-4" aria-hidden="true" /> Convert</button>
            <button onClick={() => ignoreIds(Array.from(selected))} className="btn-secondary"><X className="h-4 w-4" aria-hidden="true" /> Ignore</button>
            <label className="sr-only" htmlFor="bulk-priority">Set priority for selected</label>
            <select
              id="bulk-priority" className="input !w-auto !py-1.5" value=""
              onChange={(e) => overrideIds(Array.from(selected), e.target.value)}
            >
              <option value="">Set priority…</option>
              {OVERRIDE_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
              <option value="clear">Clear override</option>
            </select>
            <button onClick={() => setSelected(new Set())} className="btn-ghost ml-auto">Clear selection</button>
          </div>
        </div>
      )}

      {confirmNode}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="card-body text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="card-body text-sm text-slate-500">
            {filtersActive
              ? <>No documents match these filters. <button type="button" className="text-brand-600 hover:underline" onClick={clearFilters}>Clear filters</button></>
              : <>No PDFs found for this site yet. Run a crawl from the <Link to="/sites" className="text-brand-600 hover:underline">Sites</Link> page.</>}
          </div>
        ) : (
          <div className="table-wrap">
          {/* Evidence and Reasons always sit behind "Why this priority" in the
              Document cell; as the screen narrows more columns fold in there:
              Type and Year below xl, Priority, Pages and Status below md. */}
          <table className="w-full text-sm">
            <caption className="sr-only">Documents on {site?.domain ?? "this site"}, ranked by conversion priority</caption>
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th scope="col" className="px-3 py-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all convertible rows on this page" disabled={selectable.length === 0} />
                </th>
                <SortTh label="Priority" field="priority_score" sort={sort} order={order} onSort={onSort} className="hidden md:table-cell w-[130px]" />
                <SortTh label="Document" field="filename" sort={sort} order={order} onSort={onSort} />
                <th scope="col" className="hidden xl:table-cell text-left px-3 py-3 w-[110px]">Type</th>
                <SortTh label="Pages" field="page_count" sort={sort} order={order} onSort={onSort} className="hidden md:table-cell w-[70px]" />
                <SortTh label="Year" field="content_year" sort={sort} order={order} onSort={onSort} className="hidden xl:table-cell w-[70px]" />
                <th scope="col" className="hidden md:table-cell text-left px-3 py-3 w-[120px]">Status</th>
                <th scope="col" className="text-right px-3 py-3 w-[1%]"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((r) => {
                const blocked = notConvertibleReason(r);
                const inBudget = fit?.idSet.has(r.id) ?? false;
                const pages = r.page_count ?? r.estimated_pages;
                const outOfDate = isOutOfDate(r) && r.status !== "selected";
                const failed = r.conversion_state === "failed" && r.status !== "selected";
                const canConvert = !blocked && (r.status === "discovered" || r.status === "ignored" || outOfDate || failed);
                const convertLabel = failed ? "Retry" : outOfDate ? "Re-convert" : "Convert";
                return (
                  <tr key={r.id} className={selected.has(r.id) ? "bg-brand-50/50" : inBudget ? "bg-emerald-50/40" : undefined}>
                    <td className={`px-3 py-3 align-top ${inBudget ? "border-l-4 border-emerald-400" : ""}`}>
                      <span title={blocked ?? undefined} className="inline-flex">
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r.id)}
                          disabled={!!blocked}
                          aria-label={blocked ? `${r.filename} cannot be selected: ${blocked}` : `Select ${r.filename}`}
                        />
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-3 py-3 align-top">
                      <PriorityCell r={r} />
                      {inBudget && <div className="mt-1 text-[11px] font-medium text-emerald-700">in budget</div>}
                    </td>
                    <td className="px-3 py-3 align-top max-w-0 min-w-[12rem]">
                      <div className="md:hidden mb-1.5 flex flex-wrap items-center gap-2">
                        <PriorityCell r={r} />
                        {inBudget && <span className="text-[11px] font-medium text-emerald-700">in budget</span>}
                      </div>
                      <DocumentCell r={r} canonical={r.duplicate_of ? byId.get(r.duplicate_of) ?? null : null} mirrors={mirrorsOf.get(r.id) ?? []} />
                      <div className="xl:hidden mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                        <span className="md:hidden"><StatusCell r={r} /></span>
                        {r.doc_type && <TypeCell r={r} />}
                        <span className="md:hidden tabular-nums">{pages != null ? `${r.page_count == null ? "~" : ""}${pages} pages` : "pages unknown"}</span>
                        {r.content_year && <span className="tabular-nums">{r.content_year}</span>}
                      </div>
                      {((r.priority_reasons?.length ?? 0) > 0 || (r.linking_pages?.length ?? 0) > 0 || (r.page_views ?? 0) > 0) && (
                        <details className="mt-1.5 text-xs">
                          <summary className="cursor-pointer text-brand-700 hover:underline">
                            Why this priority{(r.priority_reasons?.length ?? 0) > 0 && <span className="text-slate-500"> ({r.priority_reasons!.length} reason{r.priority_reasons!.length === 1 ? "" : "s"})</span>}
                          </summary>
                          <div className="mt-1.5 space-y-2">
                            <ReasonsCell r={r} />
                            <EvidenceCell r={r} />
                          </div>
                        </details>
                      )}
                    </td>
                    <td className="hidden xl:table-cell px-3 py-3 align-top text-slate-700"><TypeCell r={r} /></td>
                    <td className="hidden md:table-cell px-3 py-3 align-top text-slate-600 tabular-nums">
                      {r.page_count != null ? r.page_count
                        : pages != null ? <span className="text-slate-400" title="Approximate">~{pages}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="hidden xl:table-cell px-3 py-3 align-top text-slate-600 tabular-nums">{r.content_year ?? <span className="text-slate-400">—</span>}</td>
                    <td className="hidden md:table-cell px-3 py-3 align-top"><StatusCell r={r} /></td>
                    <td className="px-3 py-3 align-top">
                      {!demo && (
                        <div className="flex items-center justify-end gap-1">
                          {canConvert && (
                            <button onClick={() => convertIds([r.id])} className="btn-secondary !px-2 !py-1.5 text-xs" aria-label={`${convertLabel} ${r.filename}`}>
                              <FileCheck2 className="h-3.5 w-3.5" aria-hidden="true" /> <span className="hidden sm:inline">{convertLabel}</span>
                            </button>
                          )}
                          <MoreMenu
                            label={`More actions for ${r.filename}`}
                            items={[
                              { label: "Ignore", icon: <X className="h-4 w-4" aria-hidden="true" />, onClick: () => ignoreIds([r.id]), hidden: r.status !== "discovered" },
                              ...OVERRIDE_OPTIONS.map((p) => ({
                                label: `Set priority: ${PRIORITY_META[p].label}`,
                                onClick: () => overrideIds([r.id], p),
                                disabled: r.priority_override === p,
                              })),
                              { label: "Clear priority override", onClick: () => overrideIds([r.id], "clear"), hidden: !r.priority_override },
                            ]}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        {!loading && total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        )}
      </div>

      {site && (
        <PlanSettingsDrawer
          open={settingsOpen}
          siteId={site.id}
          domain={site.domain}
          initial={plan?.settings ?? null}
          onClose={closeSettings}
          onSaved={onSettingsSaved}
          onRescore={rescore}
          onRefreshFacts={refreshFacts}
        />
      )}
    </div>
  );
}

function PlanHeader({ actions }: { actions?: React.ReactNode }) {
  return (
    <PageHeader
      title="Conversion plan"
      icon={<ListOrdered className="h-6 w-6 text-brand-600" aria-hidden="true" />}
      description="Every PDF on the site, ranked by how much it matters, with its accessibility status. Recommendations only — nothing converts until you choose."
      actions={actions}
    />
  );
}

function SortTh({ label, field, sort, order, onSort, className = "" }: {
  label: string; field: DiscoveredSortField; sort: DiscoveredSortField; order: SortOrder;
  onSort: (f: DiscoveredSortField, o: SortOrder) => void; className?: string;
}) {
  const active = sort === field;
  return (
    <th scope="col" className={`text-left px-3 py-3 ${className}`} aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}>
      <SortHeader<DiscoveredSortField> label={label} field={field} current={sort} order={order} onChange={onSort} />
    </th>
  );
}
